// Oferta de instalación PWA. La oferta se mantiene visible mientras la app NO
// esté instalada (no se descarta "para siempre"), y desaparece sola cuando la
// app corre instalada.
//
// Tres caminos, porque no todos los navegadores dan el diálogo nativo:
//  - Chrome/Edge (Android y desktop): dispara `beforeinstallprompt` y podemos
//    abrir el diálogo nativo con un click.
//  - iOS/iPadOS Safari: NO existe `beforeinstallprompt`; la instalación es
//    manual (Compartir → Agregar a inicio), así que se muestran instrucciones.
//  - Resto (o prompt nativo ya consumido): instrucciones genéricas.
import { signal } from "@preact/signals";

// El diálogo nativo está disponible (llegó beforeinstallprompt y no se usó).
export const installPromptAvailable = signal(false);
// La app ya corre instalada: no hay nada que ofrecer.
export const appInstalled = signal(typeof window !== "undefined" && isAppInstalled());
// Hay que instalar a mano: sin diálogo nativo, sólo instrucciones.
export const installHintOnly = signal(false);

let deferredPrompt = null;

/**
 * ¿La app corre instalada? `display-mode: standalone` cubre Android y desktop;
 * `navigator.standalone` es el equivalente propietario de iOS.
 */
export function isAppInstalled(win = window) {
  const mm = win.matchMedia;
  return Boolean(
    mm?.call(win, "(display-mode: standalone)")?.matches ||
    mm?.call(win, "(display-mode: minimal-ui)")?.matches ||
    win.navigator?.standalone === true
  );
}

/**
 * iOS/iPadOS con Safari, el único navegador de iOS que ofrece "Agregar a
 * inicio" (Chrome/Firefox/Edge de iOS son WebKit pero no exponen la opción).
 */
export function isIosSafari(win = window) {
  const nav = win.navigator || {};
  const ua = String(nav.userAgent || "");
  // iPadOS 13+ se reporta como MacIntel: se distingue por los puntos táctiles.
  const isIos =
    /iphone|ipad|ipod/i.test(ua) ||
    (nav.platform === "MacIntel" && (nav.maxTouchPoints || 0) > 1);
  if (!isIos) return false;
  return !/crios|fxios|edgios|opios/i.test(ua);
}

export function listenForInstallPrompt(win = window) {
  appInstalled.value = isAppInstalled(win);

  win.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event;
    installPromptAvailable.value = true;
    installHintOnly.value = false;
  });

  win.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installPromptAvailable.value = false;
    installHintOnly.value = false;
    appInstalled.value = true;
  });

  // Instalar desde el navegador no recarga la pestaña abierta: el cambio de
  // display-mode es la señal de que ya está instalada (y de que se desinstaló).
  const modeQuery = win.matchMedia?.call(win, "(display-mode: standalone)");
  modeQuery?.addEventListener?.("change", (e) => {
    appInstalled.value = e.matches || isAppInstalled(win);
  });
}

/**
 * Dispara el diálogo nativo (debe llamarse desde un click).
 * @returns {"accepted"|"dismissed"|"unavailable"}
 */
export async function promptInstall() {
  if (!deferredPrompt) return "unavailable";
  const prompt = deferredPrompt;
  deferredPrompt = null;
  installPromptAvailable.value = false;
  prompt.prompt();
  const choice = await prompt.userChoice.catch(() => null);
  const outcome = choice?.outcome || "dismissed";
  // El evento se consume en el primer prompt() y no vuelve a dispararse hasta
  // recargar. Si el usuario canceló, la oferta sigue en pantalla (la app no
  // quedó instalada) pero pasando a instrucciones, para que el botón no muera.
  if (outcome !== "accepted") installHintOnly.value = true;
  return outcome;
}
